import React, { useMemo } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Typography,
} from "@mui/material";
import { NoPhotography as NoPhotoIcon, Visibility as ViewIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
    PtiAssetType,
    PreTripInspectionListItemDto,
} from "../../../api/ptiApi";
import {
    Action,
    Column,
    DataTable,
    createColumn,
} from "../../../components/common/DataTable/DataTable";
import { getPtiStatusLabel, PTI_STATUS } from "../ptiConstants";

interface PtiHistoryListProps {
    inspections?: PreTripInspectionListItemDto[];
    isLoading: boolean;
    error?: unknown;
    assetType: PtiAssetType;
}

const getPhotoSummary = (inspection: PreTripInspectionListItemDto) =>
    `${inspection.photoCount}/${inspection.totalItemCount}`;

const PtiHistoryList: React.FC<PtiHistoryListProps> = ({
    inspections,
    isLoading,
    error,
    assetType,
}) => {
    const navigate = useNavigate();

    const columns: Column[] = useMemo(
        () => [
            createColumn("inspectionDate", "Inspection Date", {
                minWidth: 180,
                renderCell: (_value, row: PreTripInspectionListItemDto) =>
                    format(new Date(row.inspectionDate), "MMM d, yyyy h:mm a"),
            }),
            createColumn("submittedByName", "Submitted By", {
                minWidth: 160,
            }),
            createColumn("status", "Status", {
                width: 140,
                renderCell: (value) => (
                    <Chip
                        label={getPtiStatusLabel(String(value))}
                        size="small"
                        color={
                            value === "Completed"
                                ? "success"
                                : value === "Rejected"
                                  ? "error"
                                  : "warning"
                        }
                        variant="outlined"
                    />
                ),
            }),
            createColumn("reviewedByName", "Reviewed By", {
                minWidth: 140,
                renderCell: (value) =>
                    value ? (
                        <Typography variant="body2">{String(value)}</Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    ),
            }),
            createColumn("photoSummary", "Photos", {
                width: 100,
                renderCell: (_value, row: PreTripInspectionListItemDto) => {
                    const summary = getPhotoSummary(row);
                    const [captured, total] = summary.split("/").map(Number);
                    const allCaptured = captured === total && total > 0;
                    return (
                        <Chip
                            label={summary}
                            size="small"
                            color={allCaptured ? "success" : captured > 0 ? "warning" : "default"}
                            variant="outlined"
                        />
                    );
                },
            }),
            createColumn("notes", "Inspection Note", {
                flex: 1.5,
                minWidth: 200,
                renderCell: (value) =>
                    value ? (
                        <Typography variant="body2" noWrap>
                            {String(value)}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    ),
            }),
        ],
        []
    );

    const getActions = (inspection: PreTripInspectionListItemDto): Action<PreTripInspectionListItemDto>[] => [
        {
            icon: <ViewIcon fontSize="small" />,
            label: inspection.status === PTI_STATUS.Submitted ? "Review" : "View",
            onClick: () => navigate(`/pti/${inspection.id}`),
            color: "primary",
        },
    ];

    const tableItems = useMemo(
        () =>
            (inspections ?? []).map((inspection) => ({
                ...inspection,
                photoSummary: getPhotoSummary(inspection),
            })),
        [inspections]
    );

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Error loading PTI history. Please try again later.</Alert>;
    }

    if (!inspections || inspections.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <NoPhotoIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No PTI History Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No pre-trip inspections have been submitted for this {assetType.toLowerCase()} yet.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <DataTable
                items={tableItems}
                columns={columns}
                actions={getActions}
                fitContentHeight
                getRowId={(row) => row.id}
            />
        </Box>
    );
};

export default PtiHistoryList;
